import { getIsSSr } from './helpers'

// Extracted from isMobile, and modified to fit usage
function isFirefoxDesktop() {
  const matchesAndroid = navigator.userAgent.match(/Android/i)
  const matchesWebOS = navigator.userAgent.match(/webOS/i)
  const matchesIPhone = navigator.userAgent.match(/iPhone/i)
  const matchesiPad = navigator.userAgent.match(/iPad/i)
  const matchesIPod = navigator.userAgent.match(/iPod/i)
  const matchesBlackBerry = navigator.userAgent.match(/BlackBerry/i)
  const matchesWindowsPhone = navigator.userAgent.match(/Windows Phone/i)
  const matchesIOSLike = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints > 0

  const isMobileDevice =
    matchesAndroid ||
    matchesWebOS ||
    matchesIPhone ||
    matchesiPad ||
    matchesIPod ||
    matchesBlackBerry ||
    matchesWindowsPhone ||
    matchesIOSLike

  const matchesFirefox = !!navigator.userAgent.match(/firefox/i)

  return matchesFirefox && !isMobileDevice
}

// Due bug in FireFox, when if HW acceleration is used canvas is blurry
// we need to set canvas as CPU only
const shouldUseCPUOnly = getIsSSr() ? false : isFirefoxDesktop()

export function getContextWithCache(canvas: HTMLCanvasElement | null): CanvasRenderingContext2D | null {
  if (!canvas) {
    return null
  }

  const context = canvas.getContext('2d', shouldUseCPUOnly ? { willReadFrequently: true } : undefined)
  if (!context) {
    window.Sentry?.captureMessage('Context unavailable')
    return null
  }

  context._cache = {}

  const _restore = CanvasRenderingContext2D.prototype.restore
  context.restore = function () {
    this._cache = {}
    return _restore.apply(this)
  }

  context.set = function (key, value) {
    if (this._cache[key] !== value) {
      this[key] = value
      this._cache[key] = value
    }
  }

  return context
}

interface BorderRadius {
  topLeft?: number
  topRight?: number
  bottomRight?: number
  bottomLeft?: number
}

interface RoundedRectProps {
  ctx: CanvasRenderingContext2D
  x: number
  y: number
  width: number
  height: number
  borderRadius?: number | BorderRadius
  backgroundColor?: string
  shadowColor?: string
  shadowOffsetX?: number
  shadowOffsetY?: number
  shadowBlur?: number
  borderColor?: string
  borderWidth?: number
}

export function drawRoundedRect({
  ctx,
  x,
  y,
  width,
  height: heightRaw,
  borderRadius,
  backgroundColor,
  shadowColor,
  shadowOffsetY,
  shadowOffsetX,
  shadowBlur,
  borderColor,
  borderWidth = 0,
}: RoundedRectProps) {
  const height = heightRaw - borderWidth
  let topLeft = 0
  let topRight = 0
  let bottomRight = 0
  let bottomLeft = 0

  if (typeof borderRadius === 'number') {
    topLeft = topRight = bottomRight = bottomLeft = borderRadius
  } else {
    topLeft = borderRadius?.topLeft ?? 0
    topRight = borderRadius?.topRight ?? 0
    bottomRight = borderRadius?.bottomRight ?? 0
    bottomLeft = borderRadius?.bottomLeft ?? 0
  }

  const maxRadius = Math.min(width / 2, height / 2)
  topLeft = Math.min(topLeft, maxRadius)
  topRight = Math.min(topRight, maxRadius)
  bottomRight = Math.min(bottomRight, maxRadius)
  bottomLeft = Math.min(bottomLeft, maxRadius)

  ctx.save()
  if (borderWidth > 0) ctx.translate(0.5, 0.5)

  ctx.beginPath()

  ctx.moveTo(x + topLeft, y)

  ctx.lineTo(x + width - topRight, y)
  if (topRight > 0) {
    ctx.quadraticCurveTo(x + width, y, x + width, y + topRight)
  }

  ctx.lineTo(x + width, y + height - bottomRight)
  if (bottomRight > 0) {
    ctx.quadraticCurveTo(x + width, y + height, x + width - bottomRight, y + height)
  }

  ctx.lineTo(x + bottomLeft, y + height)
  if (bottomLeft > 0) {
    ctx.quadraticCurveTo(x, y + height, x, y + height - bottomLeft)
  }

  ctx.lineTo(x, y + topLeft)
  if (topLeft > 0) {
    ctx.quadraticCurveTo(x, y, x + topLeft, y)
  }

  ctx.closePath()

  if (shadowColor) {
    ctx.shadowColor = shadowColor
    if (Number.isFinite(shadowOffsetX)) ctx.set('shadowOffsetX', shadowOffsetX!)
    if (Number.isFinite(shadowOffsetY)) ctx.set('shadowOffsetY', shadowOffsetY!)
    if (Number.isFinite(shadowBlur)) ctx.set('shadowBlur', shadowBlur!)
  }

  if (backgroundColor) {
    ctx.set('fillStyle', backgroundColor)
    ctx.fill()

    if (shadowColor) {
      ctx.set('shadowColor', 'transparent')
    }
  }

  if (borderWidth > 0) {
    if (borderColor) ctx.set('strokeStyle', borderColor)
    ctx.set('lineWidth', borderWidth)
    ctx.stroke()

    if (shadowColor) {
      ctx.set('shadowColor', 'transparent')
    }
  }

  if (borderWidth > 0) ctx.translate(-0.5, -0.5)
  ctx.restore()
}

interface TriangleProps {
  ctx: CanvasRenderingContext2D
  x: number
  y: number
  size: number
  backgroundColor: string
}

export function drawTriangle({ ctx, x, y, size, backgroundColor }: TriangleProps) {
  ctx.save()
  ctx.translate(0.5, 0.5)
  ctx.beginPath()
  ctx.moveTo(x, y)
  ctx.lineTo(x + size, y + size / 2)
  ctx.lineTo(x, y + size)
  ctx.closePath()
  ctx.set('fillStyle', backgroundColor)
  ctx.fill()
  ctx.translate(-0.5, -0.5)
  ctx.restore()
}
